import { ThemeProvider, alpha, createTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import React from "react";

/**
 * Colours of the analytics charts, named by the role they serve. The same
 * roles exist in every theme: a chart painted from them reads as the same
 * chart on a dark surface and on white.
 */
export interface ChartPalette {
  /** Marks of the capability map's effect areas, by place on the curated list. */
  categorical: string[];
  /** Marks of the partnership clusters, in order. */
  clusters: string[];
  /** What is filed nowhere: the grey of "General & unplaced". */
  neutral: string;
  /** The mark of what belongs to no cluster. */
  unclustered: string;
  /** The grey of "other" bands and of structural lines. */
  muted: string;
  /** A line between marks. */
  edge: string;
  /** A line between marks that is deliberately faint. */
  edgeFaint: string;
  /** A dashed line between marks. */
  edgeSoft: string;
  /** Text that is deliberately faint, such as a footnote. */
  faintText: string;
  /** What is stated as a need. */
  need: string;
  /** What a provider enables. */
  enables: string;
  /** The home jurisdiction: its bands, its tiles, its chips. */
  home: string;
  /** The capability kind, its marks and its text. */
  capability: string;
  capabilityText: string;
  /** The category kind, its marks and its text. */
  category: string;
  categoryText: string;
  /** The organization kind, its marks and its text. */
  organization: string;
  organizationText: string;
  /** The product kind, its marks and its text. */
  product: string;
  productText: string;
  /** The programme kind, its marks and its text. */
  programme: string;
  programmeText: string;
  /**
   * The two kinds of a product's timeline: its programmes' spans and its
   * incidents' events. Validated as a pair against the raised surface.
   */
  timelineProgramme: string;
  timelineIncident: string;
  /**
   * The roles of a product's facilities on their map, and the grey of a
   * country whose facilities play several. The map's tiles are dark in
   * either mode, so the hues are the same in both: validated as a set
   * against the raised dark surface.
   */
  facilityRoles: {
    production: string;
    test: string;
    deployment: string;
    other: string;
    mixed: string;
  };
  /**
   * The parts a maker plays on its card: the manufacturer in the blue of
   * production on the facility map, the developer apart from it. Validated
   * as a pair against the raised surface of each mode.
   */
  makerRoles: { maker: string; developer: string };
  /** Text in the accent hue, readable on the surface. */
  accentText: string;
  /** Text written over a shaded fill. */
  labelOnFill: string;
  /** The colour overlays are drawn in: a hairline, a hover, a selection. */
  ink: string;
  /** The colour a shadow is cast in. */
  shadow: string;
}

declare module "@mui/material/styles" {
  interface TypeBackground {
    /** Raised surface, one tone above `paper`. */
    raised: string;
  }
  interface Palette {
    link: { main: string };
    /** Accent for marks of state: a current tab, a type chip, a ring. */
    accent: { main: string };
    /** Colours of the analytics charts, by role. */
    chart: ChartPalette;
  }
  interface PaletteOptions {
    link?: { main: string };
    accent?: { main: string };
    chart?: ChartPalette;
  }
  interface TypographyVariants {
    fontFamilyMonospace: string;
  }
  interface TypographyVariantsOptions {
    fontFamilyMonospace?: string;
  }
  interface Theme {
    radius: { surface: number; chip: number };
  }
  interface ThemeOptions {
    radius?: { surface: number; chip: number };
  }
}

/** Corner radius (px) for cards, paper, fields, buttons and tables. */
export const SURFACE_RADIUS = 8;

/** Corner radius (px) for chips. */
export const CHIP_RADIUS = 6;

/**
 * Monospace font stack. JetBrains Mono (400, 500) is loaded from Google Fonts
 * in `public/index.html`, together with Inter (400, 500, 600).
 */
export const FONT_FAMILY_MONOSPACE =
  '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

/**
 * Style object that applies the monospace family together with digits of
 * equal width. Spread it into `sx` or `style`.
 */
export const monospaceStyle = {
  fontFamily: FONT_FAMILY_MONOSPACE,
  fontVariantNumeric: "tabular-nums",
} as const;

/** Accent colour of the dark theme: `palette.accent.main`. */
export const ACCENT_COLOR = "#4c8dff";

/** Everything that differs between the themes. */
export interface ThemeTokens {
  mode: "dark" | "light";
  background: { default: string; paper: string; raised: string };
  text: { primary: string; secondary: string; disabled: string };
  divider: string;
  /** Border of controls: fields, chips and toggle groups. */
  controlBorder: string;
  /** Fill of a chip of the default colour. */
  chipFill: string;
  accent: string;
  link: string;
  /** Amber of an outlined warning chip. */
  warning: string;
  chart: ChartPalette;
}

/** Tokens of the dark theme: the app as it is shown. */
export const DARK: ThemeTokens = {
  mode: "dark",
  background: { default: "#121212", paper: "#1e1e1e", raised: "#262626" },
  text: { primary: "#ededed", secondary: "#a8a8a8", disabled: "#767676" },
  divider: "rgba(255, 255, 255, 0.06)",
  controlBorder: "rgba(255, 255, 255, 0.1)",
  chipFill: "rgba(255, 255, 255, 0.03)",
  accent: ACCENT_COLOR,
  link: "#6ea2ff",
  warning: "#f5b84a",
  chart: {
    categorical: [
      "#22d3ee",
      "#b18cff",
      "#4ade80",
      "#ff7a5c",
      "#f06fc4",
      "#a3e635",
      "#7c9cff",
    ],
    clusters: [
      "#f5a35c",
      "#4fd1a5",
      "#b18cff",
      "#e2c35a",
      "#f06fc4",
      "#ff8a7a",
      "#6ad1d1",
      "#8fd16a",
      "#c9a27e",
    ],
    neutral: "#9a9a9a",
    unclustered: "#8f8f8f",
    muted: "#8a8a8a",
    edge: "#6b6b6b",
    edgeFaint: "#4a4a4a",
    edgeSoft: "#5a5a5a",
    faintText: "#5a5a5a",
    need: "#f5b84a",
    enables: "#2dd4bf",
    home: "#2dd4bf",
    capability: "#b18cff",
    capabilityText: "#dccbff",
    category: "#f06fc4",
    categoryText: "#f9c6e8",
    organization: "#b18cff",
    organizationText: "#dccbff",
    product: "#f06fc4",
    productText: "#f9c6e8",
    programme: "#a3a3a3",
    programmeText: "#d4d4d4",
    timelineProgramme: "#0ea5c4",
    timelineIncident: "#ec5a36",
    facilityRoles: {
      production: "#3f7be0",
      test: "#c4831f",
      deployment: "#2aa78f",
      other: "#8a8a8a",
      mixed: "#d6d6d6",
    },
    makerRoles: { maker: "#3f7be0", developer: "#d0609e" },
    accentText: "#9dc1ff",
    labelOnFill: "#0f1115",
    ink: "#ffffff",
    shadow: "#000000",
  },
};

/**
 * Tokens of the light theme, for exports: printing and white slides. Marks keep
 * the hue of their dark counterparts, darkened until they reach 3:1 against
 * white; text reaches 4.5:1.
 */
export const LIGHT: ThemeTokens = {
  mode: "light",
  background: { default: "#ffffff", paper: "#ffffff", raised: "#f5f5f5" },
  text: { primary: "#1a1a1a", secondary: "#5c5c5c", disabled: "#767676" },
  divider: "rgba(0, 0, 0, 0.12)",
  controlBorder: "rgba(0, 0, 0, 0.12)",
  chipFill: "rgba(0, 0, 0, 0.04)",
  accent: "#1a62d6",
  link: "#1556c0",
  warning: "#b45309",
  chart: {
    categorical: [
      "#0891b2",
      "#8b5cf6",
      "#16a34a",
      "#e8512f",
      "#db2777",
      "#65a30d",
      "#3b6bf0",
    ],
    clusters: [
      "#d9701a",
      "#0f9f74",
      "#8b5cf6",
      "#a8830a",
      "#db2777",
      "#e0583f",
      "#1a9a9a",
      "#5f9d2a",
      "#a9794a",
    ],
    neutral: "#737373",
    unclustered: "#767676",
    muted: "#757575",
    edge: "#8c8c8c",
    edgeFaint: "#b5b5b5",
    edgeSoft: "#a3a3a3",
    faintText: "#6b6b6b",
    need: "#b45309",
    enables: "#0f766e",
    home: "#0f766e",
    capability: "#7c3aed",
    capabilityText: "#5b21b6",
    category: "#c026a3",
    categoryText: "#9d174d",
    organization: "#7c3aed",
    organizationText: "#5b21b6",
    product: "#c026a3",
    productText: "#9d174d",
    programme: "#737373",
    programmeText: "#404040",
    timelineProgramme: "#0891b2",
    timelineIncident: "#e8512f",
    facilityRoles: {
      production: "#3f7be0",
      test: "#c4831f",
      deployment: "#2aa78f",
      other: "#8a8a8a",
      mixed: "#d6d6d6",
    },
    makerRoles: { maker: "#2f6ad6", developer: "#c0478a" },
    accentText: "#1d4ed8",
    labelOnFill: "#0f1115",
    ink: "#000000",
    shadow: "#000000",
  },
};

/**
 * Builds the app theme from its tokens. `shape.borderRadius` is deliberately
 * left at MUI's default: MUI multiplies it wherever a component writes a number
 * (`sx={{ borderRadius: 2 }}`), so corners are set through component overrides
 * using `radius` instead.
 */
export const buildTheme = (tokens: ThemeTokens): Theme =>
  createTheme({
    palette: {
      mode: tokens.mode,
      primary: {
        main: "#1976d2",
      },
      secondary: {
        main: "#dc004e",
      },
      background: tokens.background,
      divider: tokens.divider,
      text: tokens.text,
      link: {
        main: tokens.link,
      },
      accent: {
        main: tokens.accent,
      },
      chart: tokens.chart,
    },
    typography: {
      fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
      fontFamilyMonospace: FONT_FAMILY_MONOSPACE,
      h4: {
        fontSize: "1.875rem",
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: "-0.01em",
      },
      h5: {
        fontSize: "1.375rem",
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: "-0.01em",
      },
      h6: {
        fontSize: "0.9375rem",
        fontWeight: 600,
        lineHeight: 1.5,
        letterSpacing: "0.01em",
      },
      subtitle1: {
        fontSize: "1rem",
        fontWeight: 500,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: "0.8125rem",
        fontWeight: 500,
        lineHeight: 1.5,
      },
    },
    radius: {
      surface: SURFACE_RADIUS,
      chip: CHIP_RADIUS,
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
          // `rounded` is applied only when the Paper is not `square`, so
          // drawers and app bars keep their square edges. Card is a Paper.
          rounded: ({ theme }) => ({
            borderRadius: theme.radius.surface,
          }),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme, ownerState }) => ({
            borderRadius: theme.radius.surface,
            // A text button of the primary colour reads in the link colour:
            // primary itself is too dark on the paper. Contained and outlined
            // buttons keep primary.
            ...(ownerState.variant === "text" &&
              ownerState.color === "primary" && {
                color: theme.palette.link.main,
                "&:hover": {
                  backgroundColor: alpha(
                    theme.palette.link.main,
                    theme.palette.action.hoverOpacity,
                  ),
                },
              }),
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.radius.surface,
          }),
          // Hover and focus keep their own colours: they are set on the root
          // with a more specific selector
          notchedOutline: {
            borderColor: tokens.controlBorder,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.radius.surface,
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          // MUI derives an opaque grey from the divider; the hairline is kept
          root: ({ theme }) => ({
            borderBottomColor: theme.palette.divider,
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme, ownerState }) => ({
            borderRadius: theme.radius.chip,
            // A chip of the default colour, filled or outlined, is a faint
            // outlined tag
            ...(ownerState.color === "default" && {
              height: 24,
              fontSize: "0.78125rem",
              backgroundColor: tokens.chipFill,
              border: `1px solid ${tokens.controlBorder}`,
            }),
            ...(ownerState.color === "warning" &&
              ownerState.variant === "outlined" && {
                color: tokens.warning,
                backgroundColor: alpha(tokens.warning, 0.08),
                border: `1px solid ${alpha(tokens.warning, 0.5)}`,
              }),
          }),
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.radius.surface,
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          // The group squares the inner corners of its buttons itself
          root: ({ theme }) => ({
            borderRadius: theme.radius.surface,
            borderColor: tokens.controlBorder,
            textTransform: "none",
            "&.Mui-selected": {
              color: theme.palette.text.primary,
              backgroundColor: alpha(theme.palette.link.main, 0.14),
              "&:hover": {
                backgroundColor: alpha(
                  theme.palette.link.main,
                  0.14 + theme.palette.action.hoverOpacity,
                ),
              },
            },
          }),
        },
      },
      MuiLink: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.link.main,
            textDecorationColor: theme.palette.link.main,
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundImage: "none",
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.radius.surface,
          }),
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: theme.radius.surface,
          }),
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: theme.radius.surface,
          }),
        },
      },
    },
  });

/** Shadow of a panel floating over a chart, cast in the theme's shadow colour. */
export const panelShadow = (theme: Theme, strength = 0.45): string =>
  `0 12px 32px ${alpha(theme.palette.chart.shadow, strength)}`;

/** The backing a name or a caption is read on, over the lines of a chart. */
export const labelBacking = (theme: Theme): string =>
  alpha(theme.palette.background.paper, 0.92);

/** The hairline that sets a group of columns off from the one before it. */
export const groupEdge = (theme: Theme): string =>
  `1px solid ${alpha(theme.palette.chart.ink, 0.12)}`;

/** App theme. */
export const darkTheme = buildTheme(DARK);

/** Theme of file exports: white surfaces, near-black text. */
export const lightTheme = buildTheme(LIGHT);

/**
 * Renders its children in the theme of an export. Light by default: an export
 * is printed or placed on a white slide. It sets no baseline, so it scopes the
 * theme to the subtree without touching the page around it.
 */
export function ExportThemeProvider({
  mode = "light",
  children,
}: {
  mode?: "dark" | "light";
  children?: React.ReactNode;
}): React.ReactElement {
  return React.createElement(
    ThemeProvider,
    { theme: mode === "light" ? lightTheme : darkTheme },
    children,
  );
}
